import type { ViewingHistoryItemResponse } from '../types/library.types';
import type { VideoCardData } from '../types/video-card.types';
import {
    CONTINUE_WATCHING_RESTART_THRESHOLD_PERCENT,
    formatLastWatchedDate,
    formatResumeTime
} from './continue-watching';

export type VideoWatchStateKind = 'in-progress' | 'watched';

/**
 * UI-ready watch state for a video card.
 */
export interface VideoWatchState {
    kind: VideoWatchStateKind;
    progressPercent: number;
    resumeSeconds: number;
    resumeLabel: string;
    lastWatchedAtUtc: string;
    lastWatchedLabel: string;
}

export type VideoWatchStateLookup = Map<string, VideoWatchState>;

const clampProgress = (value: number) => Math.min(100, Math.max(0, Math.round(value)));

/**
 * Normalize video ids and urls so API history rows can match card models.
 */
export const normalizeWatchStateKey = (value?: string | null) => value?.trim().toLowerCase() ?? '';

/**
 * Extract the video url/id value from an app watch URL.
 */
export const getVideoUrlFromWatchUrl = (watchUrl?: string | null) => {
    if (!watchUrl) {
        return null;
    }

    const queryIndex = watchUrl.indexOf('?');
    if (queryIndex < 0) {
        return null;
    }

    const params = new URLSearchParams(watchUrl.slice(queryIndex + 1));
    return params.get('v');
};

/**
 * Add or replace a resume timestamp while preserving existing watch URL context.
 */
export const buildResumeUrlForCard = (watchUrl: string, resumeSeconds: number) => {
    const queryIndex = watchUrl.indexOf('?');
    if (queryIndex < 0) {
        return watchUrl;
    }

    const path = watchUrl.slice(0, queryIndex) || '/watch';
    const params = new URLSearchParams(watchUrl.slice(queryIndex + 1));
    if (!params.has('v')) {
        return watchUrl;
    }

    params.set('t', String(Math.max(0, Math.floor(resumeSeconds))));
    return `${path}?${params.toString()}`;
};

/**
 * Convert one viewing-history row into a reusable card watch state.
 */
export const buildVideoWatchState = (
    item: ViewingHistoryItemResponse,
    restartThresholdPercent = CONTINUE_WATCHING_RESTART_THRESHOLD_PERCENT
): VideoWatchState | null => {
    if (!item.Video?.Url && !item.Video?.Id) {
        return null;
    }

    const progressPercent = clampProgress(item.ProgressPercent ?? 0);
    const isWatched = item.IsCompleted || progressPercent >= restartThresholdPercent;
    const resumeSeconds = isWatched ? 0 : Math.max(0, Math.floor(item.ResumeSeconds ?? 0));

    return {
        kind: isWatched ? 'watched' : 'in-progress',
        progressPercent,
        resumeSeconds,
        resumeLabel: formatResumeTime(resumeSeconds),
        lastWatchedAtUtc: item.LastWatchedAtUtc,
        lastWatchedLabel: formatLastWatchedDate(item.LastWatchedAtUtc)
    };
};

/**
 * Build a lookup keyed by both video id and video URL from viewing history.
 */
export const buildVideoWatchStateLookup = (items: ViewingHistoryItemResponse[]): VideoWatchStateLookup => {
    const lookup: VideoWatchStateLookup = new Map();

    [...items]
        .sort((left, right) => {
            const leftTime = Date.parse(left.LastWatchedAtUtc) || 0;
            const rightTime = Date.parse(right.LastWatchedAtUtc) || 0;
            return rightTime - leftTime;
        })
        .forEach((item) => {
            const state = buildVideoWatchState(item);
            if (!state) {
                return;
            }

            [item.Video?.Id, item.Video?.Url]
                .map(normalizeWatchStateKey)
                .filter((key) => key.length > 0)
                .forEach((key) => {
                    if (!lookup.has(key)) {
                        lookup.set(key, state);
                    }
                });
        });

    return lookup;
};

/**
 * Resolve watch state for a reusable video card.
 */
export const getVideoWatchStateForCard = (
    lookup: VideoWatchStateLookup,
    video: VideoCardData
): VideoWatchState | null => {
    const keys = [
        video.id,
        getVideoUrlFromWatchUrl(video.watchUrl)
    ].map(normalizeWatchStateKey);

    for (const key of keys) {
        if (key.length === 0) {
            continue;
        }

        const state = lookup.get(key);
        if (state) {
            return state;
        }
    }

    return null;
};

/**
 * Resolve watch state for custom cards that have an id and watch URL but do not use VideoCardData.
 */
export const getVideoWatchStateForIdentity = (
    lookup: VideoWatchStateLookup,
    videoId?: string | null,
    watchUrl?: string | null
): VideoWatchState | null => {
    const keys = [
        videoId,
        getVideoUrlFromWatchUrl(watchUrl)
    ].map(normalizeWatchStateKey);

    for (const key of keys) {
        if (key.length === 0) {
            continue;
        }

        const state = lookup.get(key);
        if (state) {
            return state;
        }
    }

    return null;
};

/**
 * Return the best watch URL for a card based on its current watch state.
 */
export const buildVideoCardWatchUrl = (video: VideoCardData, state?: VideoWatchState | null) => {
    if (state?.kind !== 'in-progress') {
        return video.watchUrl;
    }

    return buildResumeUrlForCard(video.watchUrl, state.resumeSeconds);
};
